import React from 'react';
import {
  Banknote,
  Briefcase,
  CalendarClock,
  Layers,
  MapPin,
  TrendingUp,
  Wrench,
} from 'lucide-react';
import { JobTemplate } from '../../../JobPostTemplate/Types/JobPostTemplateType';

interface Props {
  job: JobTemplate;
}

const Chip = ({ children, tone = 'slate' }: { children: React.ReactNode; tone?: 'slate' | 'cyan' | 'emerald' | 'amber' }) => {
  const tones = {
    slate: 'border-slate-700 bg-slate-800/60 text-slate-300',
    cyan: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-300',
    emerald: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
    amber: 'border-amber-500/30 bg-amber-500/10 text-amber-300',
  };
  return (
    <span className={`rounded-lg border px-2.5 py-1 text-[11px] font-bold ${tones[tone]}`}>
      {children}
    </span>
  );
};

const Section = ({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof Briefcase;
  title: string;
  children: React.ReactNode;
}) => (
  <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
    <div className="mb-4 flex items-center gap-2">
      <Icon size={14} className="text-cyan-400" />
      <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400">{title}</h3>
      <span className="h-px flex-1 bg-slate-800" />
    </div>
    {children}
  </section>
);

const Stat = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div>
    <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</p>
    <p className="mt-1 text-sm text-slate-200">{value}</p>
  </div>
);

/**
 * Detail screen for a job template reached from a data-service record.
 *
 * <p>This is built rather than reused. Job templates were only ever rendered
 * as a nested block inside an invitation card, and that block expects an
 * invitation-shaped payload rather than a job on its own, so it could not carry
 * a standalone record.</p>
 */
const JobTemplateRecordView: React.FC<Props> = ({ job }) => {
  const chips = (values?: string[]) => {
    if (!values || values.length === 0) {
      return <span className="text-sm text-slate-600">Not specified</span>;
    }
    return (
      <div className="flex flex-wrap gap-1.5">
        {values.map((value) => (
          <Chip key={value} tone="cyan">
            {value}
          </Chip>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900 to-slate-950 p-6">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Chip tone={job.jobStatus ? 'emerald' : 'slate'}>
            {job.jobStatus ? 'Active' : 'Inactive'}
          </Chip>
          {job.roleLevel && <Chip tone="cyan">{job.roleLevel}</Chip>}
          {job.workType && <Chip>{job.workType}</Chip>}
        </div>
        <h2 className="text-2xl font-black tracking-tight text-slate-50">
          {job.jobTittle || job.jobSummaryTitle || 'Untitled role'}
        </h2>
        {job.jobSummaryTitle && job.jobSummaryTitle !== job.jobTittle && (
          <p className="mt-1 text-sm font-bold text-cyan-400">{job.jobSummaryTitle}</p>
        )}
        {job.postedBy && (
          <p className="mt-2 text-xs font-bold text-slate-500">Posted by {job.postedBy}</p>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section icon={Briefcase} title="Role">
          <div className="grid grid-cols-2 gap-4">
            <Stat label="Experience" value={`${job.miniExperience ?? '—'} – ${job.maxExperience ?? '—'}`} />
            <Stat label="Team size" value={job.requiredTeamSize || '—'} />
            <Stat label="Notice period" value={job.noticePeriodDays ? `${job.noticePeriodDays} days` : '—'} />
            <Stat label="Ownership" value={job.requiredOwnerShipLevel?.join(', ') || '—'} />
          </div>
        </Section>

        <Section icon={MapPin} title="Location">
          {chips(job.location)}
        </Section>

        <Section icon={Wrench} title="Required skills">
          {chips(job.requiredTechSkills)}
        </Section>

        <Section icon={Layers} title="Domains">
          {chips(job.requiredDomain)}
        </Section>

        <Section icon={TrendingUp} title="Expected impact">
          {chips(job.expectedImpactLevel)}
        </Section>

        <Section icon={Banknote} title="Compensation">
          <Stat
            label="Minimum budget"
            value={job.minBudgetHandled || '—'}
          />
          {job.compensationOffer ? (
            <div className="mt-3">
              <Stat label="Offer" value={job.compensationOffer.toLocaleString()} />
            </div>
          ) : null}
        </Section>
      </div>

      <Section icon={Briefcase} title="Description">
        {job.jobDescription ? (
          <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-200">
            {job.jobDescription}
          </p>
        ) : (
          <p className="text-sm text-slate-600">No description provided.</p>
        )}
      </Section>

      <Section icon={CalendarClock} title="Record">
        <div className="grid gap-4 sm:grid-cols-3">
          <Stat label="Created" value={job.createdAt?.slice(0, 10) || '—'} />
          <Stat label="Updated" value={job.updatedAt?.slice(0, 10) || '—'} />
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Job id</p>
            <p className="mt-1 break-all font-mono text-[11px] text-sky-300">{job.id}</p>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default JobTemplateRecordView;
